import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useOutlet } from 'react-router-dom';
import { cerrarSesion } from '../../lib/cerrarSesion';
import { SetupProgress } from './SetupProgress';
import { SETUP_TOTAL_STEPS } from './accountSetup';
import { SETUP_SLIDE_MS, StepPhaseContext, type StepDir } from './setupStepPhase';
import { ONBOARDING_V2 } from './onboardingV2Routes';

/**
 * Layout común de los 4 pasos del "Account setup" (ADR-736).
 *
 * Antes cada paso era una ruta independiente que se desvanecía (fade, ADR-715). Ahora
 * los 4 pasos son **rutas hijas de este layout**, que:
 * - Fija arriba el **header** (back + barra de progreso + "N de N"): un único
 *   `SetupProgress` persistente cuyo `step` sale de la URL, así la **barra se anima
 *   sola** al avanzar/retroceder (no salta ni viaja con la pantalla).
 * - Orquesta la transición **slide/push**: mantiene montadas la pantalla saliente y
 *   la entrante durante `SETUP_SLIDE_MS` y le pasa a cada una su fase (`enter`/`leave`)
 *   y el sentido (`forward`/`back`) por `StepPhaseContext`. El sentido se deduce de
 *   comparar el nº de paso destino vs. el de origen (no hace falta que las páginas
 *   sepan nada de la animación: navegan normal).
 *
 * `generando` y `resultados` quedan **fuera** de este layout (no tienen header de
 * progreso ni slide; son pantallas propias con su fade).
 */

const STEP_BY_PATH: Record<string, number> = {
  [ONBOARDING_V2.setup]: 1,
  [ONBOARDING_V2.setupCategoria]: 2,
  [ONBOARDING_V2.setupContenido]: 3,
  [ONBOARDING_V2.setupProducto]: 4,
};

const PATH_BY_STEP: Record<number, string> = {
  1: ONBOARDING_V2.setup,
  2: ONBOARDING_V2.setupCategoria,
  3: ONBOARDING_V2.setupContenido,
  4: ONBOARDING_V2.setupProducto,
};

const stepOf = (pathname: string): number => STEP_BY_PATH[pathname] ?? 1;

interface StepState {
  curKey: string;
  curEl: ReactNode;
  prevKey: string | null;
  prevEl: ReactNode;
  dir: StepDir;
  animating: boolean;
}

export function AccountSetupLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const outlet = useOutlet();
  const step = stepOf(location.pathname);

  // Snapshot del `outlet` para poder mantener montada la pantalla saliente mientras
  // se anima (React Router sólo monta la ruta activa; capturamos el elemento).
  const outletRef = useRef<ReactNode>(outlet);
  outletRef.current = outlet;

  // Cuántas entradas de historial apiló EL PROPIO cuestionario por encima de aquella
  // con la que se entró. Es lo único que habilita un `navigate(-1)` seguro: garantiza
  // que la entrada de abajo es el paso anterior y no una pantalla ajena.
  // (`window.history.length` NO sirve — cuenta toda la sesión de la pestaña, así que da
  // >1 también cuando se entró por deep-link o pestaña restaurada, y ahí el back
  // expulsaba al usuario fuera del onboarding. Mismo criterio que ADR-144: nunca
  // `navigate(-1)` a ciegas; acá extendido a un wizard de varios pasos, donde el
  // `location.key !== 'default'` de aquel ADR sólo cubre la primera entrada.)
  const pushedSteps = useRef(0);
  const lastPath = useRef(location.pathname);

  const [state, setState] = useState<StepState>(() => ({
    curKey: location.pathname,
    curEl: outlet,
    prevKey: null,
    prevEl: null,
    dir: 'forward',
    animating: false,
  }));

  // Al cambiar de ruta: arranca la transición (guarda la saliente como `prev`,
  // la entrante como `cur`) y calcula el sentido según el nº de paso.
  useEffect(() => {
    // Contador de entradas propias: avanzar apila una, retroceder la saca (sea por
    // nuestro botón o por el back del navegador, que también pasa por acá).
    // Fuera del `setState` a propósito: el updater puede re-ejecutarse (StrictMode) y
    // mutar un ref ahí contaría de más.
    if (lastPath.current !== location.pathname) {
      pushedSteps.current =
        stepOf(location.pathname) > stepOf(lastPath.current)
          ? pushedSteps.current + 1
          : Math.max(0, pushedSteps.current - 1);
      lastPath.current = location.pathname;
    }
    setState((s) => {
      if (s.curKey === location.pathname) {
        return { ...s, curEl: outletRef.current };
      }
      const dir: StepDir = stepOf(location.pathname) > stepOf(s.curKey) ? 'forward' : 'back';
      return {
        curKey: location.pathname,
        curEl: outletRef.current,
        prevKey: s.curKey,
        prevEl: s.curEl,
        dir,
        animating: true,
      };
    });
  }, [location.pathname]);

  // Terminada la animación: desmonta la pantalla saliente y vuelve a reposo.
  const timer = useRef<number>();
  useEffect(() => {
    if (!state.animating) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setState((s) => ({ ...s, prevKey: null, prevEl: null, animating: false }));
    }, SETUP_SLIDE_MS + 40);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [state.animating, state.curKey]);

  // Volver desde el paso 1 = **salir del onboarding**, y salir del onboarding es
  // DESLOGUEARSE. Detrás del paso 1 sólo está el registro, y volver ahí con la sesión
  // viva no tiene sentido: la guardia de `/auth` ve sesión y rebota al home — que es
  // exactamente el bug reportado (antes se intentaba esquivarla con un `state.fromSetup`
  // que ya no lee nadie, porque `Bienvenida.tsx` se fusionó en `/auth?mode=signup`).
  // Cerrando la sesión, el carrusel se muestra como corresponde y el usuario puede
  // registrarse con otra cuenta. Ver ADR-2026-08-03-volver-en-el-paso-1-desloguea.
  const saliendo = useRef(false);
  const salirDelOnboarding = async () => {
    if (saliendo.current) return; // el signOut tarda: un segundo toque no debe reentrar
    saliendo.current = true;
    // El respaldo `scope: 'local'` y el borrado de las respuestas del cuestionario
    // —que nacieron acá— viven ahora en `lib/cerrarSesion`, que además limpia el
    // resto del estado de la cuenta y recarga el documento
    // (ADR-2026-08-20-el-cache-de-sesion-cruzaba-cuentas). Este flujo es el que
    // MÁS lo necesita: sale para que entre otra cuenta.
    await cerrarSesion(ONBOARDING_V2.bienvenida);
  };

  // Dentro del cuestionario, volver retrocede un paso (slide inverso). Preferimos el
  // POP del historial cuando la entrada previa es nuestra (no hace crecer el historial,
  // así el back del navegador sigue siendo coherente); si no, caemos a la ruta del paso
  // anterior. Ver ADR-793.
  const handleBack = () => {
    if (step <= 1) {
      void salirDelOnboarding();
      return;
    }
    // Sólo hacemos POP si la entrada de abajo la apilamos nosotros; si no (deep-link o
    // pestaña restaurada en un paso >1) navegamos por ruta.
    if (pushedSteps.current > 0) navigate(-1);
    else navigate(PATH_BY_STEP[step - 1]);
  };

  return (
    <div className="bg-white font-sans flex justify-center">
      {/* Contenedor principal: máx. 800px, se adapta hacia abajo (full-width en mobile). */}
      <div className="pwa-screen-fixed relative flex h-[100svh] w-full max-w-[800px] flex-col overflow-hidden">
        {/* Header fijo: back + barra de progreso (animada) + "N de N". */}
        <div className="relative z-30 shrink-0 px-5 pb-2 pt-[calc(env(safe-area-inset-top)+20px)] md:px-8 md:pt-8">
          <SetupProgress step={step} total={SETUP_TOTAL_STEPS} onBack={handleBack} />
        </div>

        {/* Región de contenido: apila la pantalla saliente y la entrante (slide). */}
        <div className="relative min-h-0 flex-1 overflow-hidden">
          {state.prevEl && state.prevKey ? (
            <StepPhaseContext.Provider key={state.prevKey} value={{ phase: 'leave', dir: state.dir }}>
              {state.prevEl}
            </StepPhaseContext.Provider>
          ) : null}
          <StepPhaseContext.Provider
            key={state.curKey}
            value={{ phase: state.animating ? 'enter' : 'idle', dir: state.dir }}
          >
            {state.curEl}
          </StepPhaseContext.Provider>
        </div>
      </div>
    </div>
  );
}

export default AccountSetupLayout;
